<template>
  <vs-popup title="Dr. de Conexão" :active.sync="isActive" @close="close()">
    <div class="d-flex flex-column" style="gap:16px; padding:10px;">
      <div>
        <label style="font-size: 13px; font-weight: 500; margin-bottom: 8px; display: block;">
          Selecione a Conexão
        </label>
        <vs-select
          v-model="selectedInstance"
          placeholder="Escolha uma conexão"
          style="width:100%;"
          :disabled="loading"
        >
          <vs-select-item
            :key="instance.id"
            :value="instance.id"
            :text="instance.name"
            v-for="instance in instances"
          />
        </vs-select>
      </div>

      <div v-if="loading" class="d-flex justify-center" style="padding: 20px;">
        <vs-progress indeterminate color="primary">Executando diagnóstico...</vs-progress>
      </div>

      <div v-if="diagnosticResult && !loading" class="diagnostic-result">
        <div class="result-section">
          <h4>Status da Conexão</h4>
          <p><strong>Nome:</strong> {{ diagnosticResult.instance_name }}</p>
          <p><strong>Tipo:</strong> {{ diagnosticResult.connection_type }}</p>
          <p><strong>Status:</strong> <span :class="getStatusClass(diagnosticResult.status)">{{ diagnosticResult.status }}</span></p>
        </div>

        <div class="result-section" v-if="diagnosticResult.summary">
          <h4>Resumo</h4>
          <p>{{ diagnosticResult.summary }}</p>
        </div>

        <div class="result-section" v-if="diagnosticResult.problems && diagnosticResult.problems.length">
          <h4>Problemas Detectados</h4>
          <ul>
            <li v-for="(problem, index) in diagnosticResult.problems" :key="index">{{ problem }}</li>
          </ul>
        </div>

        <div class="result-section" v-if="diagnosticResult.instructions && diagnosticResult.instructions.length">
          <h4>Instruções</h4>
          <ul>
            <li v-for="(instruction, index) in diagnosticResult.instructions" :key="index">{{ instruction }}</li>
          </ul>
        </div>

        <div class="d-flex justify-center" style="margin-top: 16px;">
          <vs-button type="border" @click="copyResult">
            Copiar Resultado
          </vs-button>
        </div>
      </div>

      <div class="d-flex justify-end" v-if="!loading && !diagnosticResult">
        <vs-button type="filled" @click="executeDiagnostic" :disabled="!selectedInstance">
          Executar Diagnóstico
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import {mapGetters} from 'vuex';

export default {
  name: 'DiagnoseInstance',
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    tenantName: {
      type: String,
      required: true
    },
    instances: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapGetters(['token'])
  },
  data() {
    return {
      selectedInstance: null,
      diagnosticResult: null,
      loading: false
    }
  },
  methods: {
    close() {
      this.selectedInstance = null;
      this.diagnosticResult = null;
      this.loading = false;
      this.$emit('isActiveFalse');
    },
    getStatusClass(status) {
      const statusLower = (status || '').toLowerCase();
      if (statusLower.includes('ok') || statusLower.includes('conectad') || statusLower.includes('ativo')) {
        return 'status-success';
      } else if (statusLower.includes('erro') || statusLower.includes('falha') || statusLower.includes('desconectad')) {
        return 'status-error';
      } else if (statusLower.includes('atenção') || statusLower.includes('aviso')) {
        return 'status-warning';
      }
      return 'status-info';
    },
    executeDiagnostic() {
      if (!this.selectedInstance) return;
      
      this.loading = true;
      this.diagnosticResult = null;

      this.$store.dispatch('diagnoseInstance', {
        instanceId: this.selectedInstance,
        tenantName: this.tenantName,
        token: this.token
      }).then(response => {
        this.diagnosticResult = response.data;
        this.loading = false;
        this.$vs.notify({
          color: 'success',
          title: 'Diagnóstico Concluído!',
          text: 'O diagnóstico foi executado com sucesso.'
        });
      }).catch(exception => {
        console.error(exception);
        this.loading = false;
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Ocorreu um erro ao executar o diagnóstico! Entre em contato com o time técnico.'
        });
      });
    },
    copyResult() {
      if (!this.diagnosticResult) return;

      let text = `=== DIAGNÓSTICO DE CONEXÃO ===\n\n`;
      text += `Nome: ${this.diagnosticResult.instance_name}\n`;
      text += `Tipo: ${this.diagnosticResult.connection_type}\n`;
      text += `Status: ${this.diagnosticResult.status}\n\n`;
      
      if (this.diagnosticResult.summary) {
        text += `RESUMO:\n${this.diagnosticResult.summary}\n\n`;
      }
      
      if (this.diagnosticResult.problems && this.diagnosticResult.problems.length) {
        text += `PROBLEMAS DETECTADOS:\n`;
        this.diagnosticResult.problems.forEach((problem, index) => {
          text += `${index + 1}. ${problem}\n`;
        });
        text += `\n`;
      }
      
      if (this.diagnosticResult.instructions && this.diagnosticResult.instructions.length) {
        text += `INSTRUÇÕES:\n`;
        this.diagnosticResult.instructions.forEach((instruction, index) => {
          text += `${index + 1}. ${instruction}\n`;
        });
      }

      navigator.clipboard.writeText(text).then(() => {
        this.$vs.notify({
          color: 'success',
          title: 'Copiado!',
          text: 'Resultado copiado para a área de transferência.'
        });
      }).catch(() => {
        this.$vs.notify({
          color: 'warning',
          title: 'Aviso',
          text: 'Não foi possível copiar automaticamente. Por favor, copie manualmente.'
        });
      });
    }
  },
  watch: {
    isActive(value) {
      if (!value) {
        this.selectedInstance = null;
        this.diagnosticResult = null;
        this.loading = false;
      }
    },
    selectedInstance(){
      this.diagnosticResult = null;
    }
  }
}
</script>

<style lang="scss" scoped>
.d-flex {
  display: flex;
}

.flex-column {
  flex-direction: column;
}

.justify-end {
  justify-content: flex-end;
}

.justify-center {
  justify-content: center;
}

.diagnostic-result {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
}

.result-section {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
    border-bottom: 2px solid #ddd;
    padding-bottom: 4px;
  }

  p {
    font-size: 13px;
    color: #666;
    margin: 4px 0;
  }

  ul {
    list-style: none;
    padding-left: 0;
    margin: 8px 0;

    li {
      font-size: 13px;
      color: #666;
      margin: 4px 0;
      padding-left: 20px;
      position: relative;

      &:before {
        content: "•";
        position: absolute;
        left: 8px;
        color: #666;
      }
    }
  }
}

.status-success {
  color: #2e7d32;
  font-weight: 600;
}

.status-error {
  color: #d32f2f;
  font-weight: 600;
}

.status-warning {
  color: #f57c00;
  font-weight: 600;
}

.status-info {
  color: #1976d2;
  font-weight: 600;
}
</style>
